/**
 * What a section contributes to the table of contents, or null for nothing.
 *
 * A deliberate mirror of tocEntryFor in backend/utils/report-helpers.js. The
 * preview carried its own third rule and disagreed with both renderers: it
 * listed spacers and horizontal rules, numbered every entry including those,
 * and printed a heading band as the bare word "Heading".
 *
 * verify-report-bands.js compares the two implementations against the same
 * sections, so this file drifting from the backend one fails the suite rather
 * than showing the author a contents page the document will not have.
 */

export const TOC_SKIP_TYPES = ['table-of-contents', 'page-break', 'horizontal-line', 'image', 'spacer'];
export const TOC_MAX_LEVEL = 3;

/** Rich text flattened to one line — the mirror of flattenToLine. */
export function flattenToLine(value) {
  return String(value || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|h[1-6]|tr|td|th|blockquote)\s*>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

export function tocLevel(value, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(TOC_MAX_LEVEL, Math.max(1, Math.round(n)));
}

export function tocEntryFor(section) {
  const s = section || {};
  const cfg = s.config || {};
  if (TOC_SKIP_TYPES.includes(s.type)) return null;

  if (s.type === 'heading') {
    // A heading band is the one section carrying both its own text and its own
    // level. Its text lives in config.html once anyone has typed in the editor
    // and in config.text before that — never in config.heading.
    const label = flattenToLine(cfg.html || cfg.text || '');
    if (!label) return null;
    return { label, level: tocLevel(cfg.level, 2) };
  }

  // An empty heading that is PRESENT was cleared on purpose, and means the
  // section renders without one.
  const hasExplicitHeading = Object.prototype.hasOwnProperty.call(cfg, 'heading');
  // Trimmed, not flattened: every other band's heading is typed into a plain
  // input, where '<' is a less-than sign.
  const heading = String(cfg.heading || '').trim();
  if (hasExplicitHeading && !heading) return null;
  const label = heading || String(s.label || '').trim();
  if (!label) return null;
  return { label, level: 1 };
}

/**
 * Everything a section contributes, in document order.
 *
 * A Custom Section keeps its real structure in the heading COMPONENTS inside
 * it, not in a heading of its own, so walking sections alone listed four lines
 * for a template with twenty-three headings in it.
 */
export function tocEntriesFor(section) {
  const s = section || {};
  const own = tocEntryFor(s);
  const out = own ? [own] : [];

  const components = (s.config || {}).components;
  if (!Array.isArray(components)) return out;
  for (const comp of components) {
    if (!comp || comp.type !== 'heading') continue;
    const cfg = comp.config || {};
    const label = flattenToLine(cfg.html || cfg.text || '');
    if (!label) continue;
    out.push({ label, level: tocLevel(cfg.level, 2) });
  }
  return out;
}

/**
 * The contents, as the preview shows it: entries in order, each with the
 * number it will carry (top-level only) or null.
 */
export function tocEntries(sections) {
  const all = sections || [];
  const tocIdx = all.findIndex(s => s.type === 'table-of-contents');
  const out = [];
  let num = 1;
  all.forEach((s, i) => {
    if (i <= tocIdx) return;
    for (const entry of tocEntriesFor(s)) {
      out.push({ id: s.id, label: entry.label, level: entry.level,
                 number: entry.level === 1 ? num : null });
      if (entry.level === 1) num += 1;
    }
  });
  return out;
}
